/* eslint-disable sonarjs/deprecation */
import type { ILogger } from '@rudderstack/analytics-js-common/types/Logger';
import { CONFIG_MANAGER } from '@rudderstack/analytics-js-common/constants/loggerContexts';
import { batch } from '@preact/signals-core';
import { isDefined, isUndefined } from '@rudderstack/analytics-js-common/utilities/checks';
import { isSDKRunningInChromeExtension } from '@rudderstack/analytics-js-common/utilities/detect';
import type { CookieOptions } from '@rudderstack/analytics-js-common/types/Storage';
import type {
  DeliveryType,
  StorageStrategy,
} from '@rudderstack/analytics-js-common/types/LoadOptions';
import {
  DEFAULT_PRE_CONSENT_EVENTS_DELIVERY_TYPE,
  DEFAULT_PRE_CONSENT_STORAGE_STRATEGY,
} from '@rudderstack/analytics-js-common/constants/consent';
import { isObjectLiteralAndNotNull } from '@rudderstack/analytics-js-common/utilities/object';
import type {
  ConsentManagementMetadata,
  ConsentResolutionStrategy,
} from '@rudderstack/analytics-js-common/types/Consent';
import { clone } from 'ramda';
import type { PluginName } from '@rudderstack/analytics-js-common/types/PluginsManager';
import { isValidURL, removeDuplicateSlashes } from '@rudderstack/analytics-js-common/utilities/url';
import { removeLeadingPeriod } from '@rudderstack/analytics-js-common/utilities/string';
import type { Destination } from '@rudderstack/analytics-js-common/types/Destination';
import { getDestinationUserFriendlyId } from '@rudderstack/analytics-js-common/utilities/destinations';
import { MODULE_TYPE, APP_VERSION } from '../../../constants/app';
import { BUILD_TYPE, DEFAULT_CONFIG_BE_URL } from '../../../constants/urls';
import { state } from '../../../state';
import {
  INVALID_CONFIG_URL_WARNING,
  STORAGE_DATA_MIGRATION_OVERRIDE_WARNING,
  UNSUPPORTED_BEACON_API_WARNING,
  UNSUPPORTED_PRE_CONSENT_EVENTS_DELIVERY_TYPE,
  UNSUPPORTED_PRE_CONSENT_STORAGE_STRATEGY,
  DEPRECATED_PRE_CONSENT_STORAGE_STRATEGY,
  UNSUPPORTED_STORAGE_ENCRYPTION_VERSION_WARNING,
  SERVER_SIDE_COOKIE_FEATURE_OVERRIDE_ERROR,
  SERVER_SIDE_COOKIE_DATA_SERVICE_HOST_ERROR,
  DATA_SERVER_URL_INVALID_ERROR,
} from '../../../constants/logMessages';
import {
  isErrorReportingEnabled,
  isMetricsReportingEnabled,
} from '../../utilities/statsCollection';
import { getHostname, removeTrailingSlashes } from '../../utilities/url';
import type { ConfigResponseDestinationItem, SourceConfigResponse } from '../types';
import {
  CUSTOM_DEVICE_MODE_DESTINATION_DISPLAY_NAME,
  DEFAULT_DATA_SERVICE_ENDPOINT,
  DEFAULT_STORAGE_ENCRYPTION_VERSION,
  StorageEncryptionVersionsToPluginNameMap,
} from '../constants';
import { domain } from '../../../services/StoreManager/top-domain';
import {
  getDataServiceUrl,
  isDomainMatch,
  isWebpageDataServiceHost,
  isWebpageTopLevelDomain,
  validateStorageOptions,
} from './validate';
import { getConsentManagementData } from '../../utilities/consent';

/**
 * Determines the SDK URL
 * @returns sdkURL
 */
const getSDKUrl = (): string | undefined => {
  // First try the new method of getting the SDK URL
  // from the script tag
  const scriptTag = document.querySelector<HTMLScriptElement>('script[data-rsa-write-key]');
  if (scriptTag && scriptTag.dataset.rsaWriteKey === state.lifecycle.writeKey.value) {
    return scriptTag.src;
  }

  // If the new method fails, try the old method
  // TODO: We need to remove this once all the customers upgrade to the
  // latest SDK loading snippet
  const scripts = document.getElementsByTagName('script');
  const sdkFileNameRegex = /(?:^|\/)rsa(\.min)?\.js$/;

  // eslint-disable-next-line no-restricted-syntax
  for (const script of scripts) {
    const src = script.getAttribute('src');
    if (src && sdkFileNameRegex.test(src)) {
      return src;
    }
  }

  return undefined;
};

/**
 * Updates the reporting state variables from the source config data
 * @param res Source config
 * @param logger Logger instance
 */
const updateReportingState = (res: SourceConfigResponse): void => {
  state.reporting.isErrorReportingEnabled.value =
    isErrorReportingEnabled(res.source.config) && !isSDKRunningInChromeExtension();
  state.reporting.isMetricsReportingEnabled.value = isMetricsReportingEnabled(res.source.config);
};

/**
 * Resolves the webpage's browser-verified cookie domain, along with the same domain carrying
 * the current port so that a derived data service URL still points at the webpage's own server.
 * Note: the storage module probes the same cookie levels again while building the default
 * cookie options; both belong in the refactor noted below.
 */
const getWebpageTopDomain = (): { topDomain: string; topHost: string } => {
  const topDomain = domain(globalThis.location.href);
  const { port } = globalThis.location;

  return { topDomain, topHost: topDomain && port ? `${topDomain}:${port}` : topDomain };
};

/**
 * Determines whether the cookies the data service would set are ones the webpage can read
 * back, and reports the reason when they are not.
 */
const isServerSideCookiesUsable = (
  dataServiceHostname: string,
  providedCookieDomain: string | undefined,
  webpage: { hostname: string; topDomain: string },
  sameDomainCookiesOnly: boolean,
  logger: ILogger,
): boolean => {
  // The data service sets the cookies, so it has to be a host whose cookies the webpage
  // can read back. In the same domain cookies mode they are host-only, hence the exact host
  if (!isWebpageDataServiceHost(dataServiceHostname, webpage.topDomain, sameDomainCookiesOnly)) {
    logger.error(
      SERVER_SIDE_COOKIE_DATA_SERVICE_HOST_ERROR(
        CONFIG_MANAGER,
        dataServiceHostname,
        webpage.hostname,
      ),
    );
    return false;
  }

  // The cookie domain is dropped altogether in the same domain cookies mode
  if (sameDomainCookiesOnly || !isDefined(providedCookieDomain)) {
    return true;
  }

  /**
   * A provided cookie domain only works if the data service is able to set it and the webpage
   * falls under it, ex: cookie domain 'random.com', or a sibling subdomain of the webpage
   */
  // Hostnames reach us already lowercased, a configured cookie domain does not
  const cookieDomain = removeLeadingPeriod(providedCookieDomain as string).toLowerCase();

  /**
   * The cookie domain also has to sit at or below the browser-verified domain of the webpage.
   * Anything above it is a public suffix, ex: 'co.uk', which the browser refuses to scope
   * cookies to however well it matches as a string suffix.
   */
  if (
    !isDomainMatch(dataServiceHostname, cookieDomain) ||
    !isDomainMatch(webpage.hostname, cookieDomain) ||
    (webpage.topDomain && !isDomainMatch(cookieDomain, webpage.topDomain))
  ) {
    logger.error(
      SERVER_SIDE_COOKIE_FEATURE_OVERRIDE_ERROR(
        CONFIG_MANAGER,
        providedCookieDomain,
        webpage.hostname,
      ),
    );
    return false;
  }

  return true;
};

const getServerSideCookiesStateData = (logger: ILogger) => {
  const {
    useServerSideCookies,
    dataServiceEndpoint,
    storage: storageOptsFromLoad,
    setCookieDomain,
    sameDomainCookiesOnly,
  } = state.loadOptions.value;

  let cookieOptions = storageOptsFromLoad?.cookie as CookieOptions;
  let sscEnabled = false;
  let finalDataServiceUrl: string | undefined;
  if (useServerSideCookies) {
    sscEnabled = useServerSideCookies;
    const providedCookieDomain = cookieOptions.domain ?? setCookieDomain;
    /**
     * Based on the following conditions, we decide whether to use the exact domain or not to determine the data service URL:
     * 1. If the cookie domain is provided and it is not a top-level domain, then use the exact domain
     * 2. If the sameDomainCookiesOnly flag is set to true, then use the exact domain
     */
    const useExactDomain =
      (isDefined(providedCookieDomain) &&
        !isWebpageTopLevelDomain(removeLeadingPeriod(providedCookieDomain as string))) ||
      (sameDomainCookiesOnly as boolean);

    const { hostname: webpageHostname } = globalThis.location;
    const { topDomain: webpageTopDomain, topHost: webpageTopHost } = getWebpageTopDomain();

    const dataServiceUrl = getDataServiceUrl(
      dataServiceEndpoint ?? DEFAULT_DATA_SERVICE_ENDPOINT,
      useExactDomain,
      webpageTopHost,
    );

    // The hostname is unavailable on legacy JS engines without the URL polyfill,
    // in which case the data service host cannot be verified
    const dataServiceHostname = getHostname(dataServiceUrl);

    if (isValidURL(dataServiceUrl) && dataServiceHostname) {
      finalDataServiceUrl = removeTrailingSlashes(dataServiceUrl) as string;

      sscEnabled = isServerSideCookiesUsable(
        dataServiceHostname,
        providedCookieDomain,
        { hostname: webpageHostname, topDomain: webpageTopDomain },
        sameDomainCookiesOnly as boolean,
        logger,
      );

      // If the current hostname is different from the data service hostname, then it is a
      // cross-site request. Cookies are not isolated by port, hence it plays no part here.
      // For server-side cookies to work, we need to set the SameSite=None and Secure attributes
      // One round of cookie options manipulation is taking place here
      // Based on these(setCookieDomain/storage.cookie or sameDomainCookiesOnly) two load-options, final cookie options are set in the storage module
      // TODO: Refactor the cookie options manipulation logic in one place
      if (sscEnabled && webpageHostname !== dataServiceHostname) {
        cookieOptions = {
          ...cookieOptions,
          samesite: 'None',
          secure: true,
        };
      }
    } else {
      // A malformed dataServiceEndpoint reaches here, ex: a typo in an absolute URL
      logger.error(DATA_SERVER_URL_INVALID_ERROR(CONFIG_MANAGER, dataServiceUrl));
      sscEnabled = false;
    }
  }

  return {
    sscEnabled,
    cookieOptions,
    finalDataServiceUrl,
  };
};

const updateStorageStateFromLoadOptions = (logger: ILogger): void => {
  const { storage: storageOptsFromLoad } = state.loadOptions.value;

  // Only reported here. The applicable default depends on the consent phase, so the unsupported
  // values are resolved in the store manager.
  validateStorageOptions(storageOptsFromLoad, CONFIG_MANAGER, logger);

  let storageEncryptionVersion = storageOptsFromLoad?.encryption?.version;
  const encryptionPluginName =
    storageEncryptionVersion && StorageEncryptionVersionsToPluginNameMap[storageEncryptionVersion];

  if (!isUndefined(storageEncryptionVersion) && isUndefined(encryptionPluginName)) {
    // set the default encryption plugin
    logger.warn(
      UNSUPPORTED_STORAGE_ENCRYPTION_VERSION_WARNING(
        CONFIG_MANAGER,
        storageEncryptionVersion,
        StorageEncryptionVersionsToPluginNameMap,
        DEFAULT_STORAGE_ENCRYPTION_VERSION,
      ),
    );
    storageEncryptionVersion = DEFAULT_STORAGE_ENCRYPTION_VERSION;
  } else if (isUndefined(storageEncryptionVersion)) {
    storageEncryptionVersion = DEFAULT_STORAGE_ENCRYPTION_VERSION;
  }

  // Allow migration only if the configured encryption version is the default encryption version
  const configuredMigrationValue = storageOptsFromLoad?.migrate;
  const finalMigrationVal =
    (configuredMigrationValue as boolean) &&
    storageEncryptionVersion === DEFAULT_STORAGE_ENCRYPTION_VERSION;

  if (configuredMigrationValue === true && finalMigrationVal !== configuredMigrationValue) {
    logger.warn(
      STORAGE_DATA_MIGRATION_OVERRIDE_WARNING(
        CONFIG_MANAGER,
        storageEncryptionVersion,
        DEFAULT_STORAGE_ENCRYPTION_VERSION,
      ),
    );
  }

  const { sscEnabled, finalDataServiceUrl, cookieOptions } = getServerSideCookiesStateData(logger);

  batch(() => {
    state.storage.cookie.value = cookieOptions;

    state.serverCookies.isEnabledServerSideCookies.value = sscEnabled;
    state.serverCookies.dataServiceUrl.value = finalDataServiceUrl;

    state.storage.encryptionPluginName.value =
      StorageEncryptionVersionsToPluginNameMap[storageEncryptionVersion as string];

    state.storage.migrate.value = finalMigrationVal;
  });
};

const updateConsentsStateFromLoadOptions = (logger: ILogger): void => {
  const { provider, consentManagerPluginName, initialized, enabled, consentsData } =
    getConsentManagementData(state.loadOptions.value.consentManagement, logger);

  // Pre-consent
  const preConsentOpts = state.loadOptions.value.preConsent;

  const configuredStrategy = preConsentOpts?.storage?.strategy;
  if (isDefined(configuredStrategy)) {
    logger.warn(DEPRECATED_PRE_CONSENT_STORAGE_STRATEGY(CONFIG_MANAGER));
  }

  // The storage load API options drive the pre-consent phase only when it is explicitly enabled,
  // so an existing configuration keeps persisting nothing before consent is given. Enabling it
  // takes precedence over the deprecated strategy, otherwise a migration that leaves the strategy
  // in place would silently do nothing.
  let storageStrategy: StorageStrategy | undefined;
  if (preConsentOpts?.storage?.enabled !== true) {
    storageStrategy = configuredStrategy ?? DEFAULT_PRE_CONSENT_STORAGE_STRATEGY;

    const StorageStrategies = ['none', 'session', 'anonymousId'];
    if (!StorageStrategies.includes(storageStrategy as string)) {
      storageStrategy = DEFAULT_PRE_CONSENT_STORAGE_STRATEGY;

      logger.warn(
        UNSUPPORTED_PRE_CONSENT_STORAGE_STRATEGY(
          CONFIG_MANAGER,
          configuredStrategy,
          DEFAULT_PRE_CONSENT_STORAGE_STRATEGY,
        ),
      );
    }
  }

  let eventsDeliveryType: DeliveryType =
    preConsentOpts?.events?.delivery ?? DEFAULT_PRE_CONSENT_EVENTS_DELIVERY_TYPE;
  const deliveryTypes = ['immediate', 'buffer'];
  if (isDefined(eventsDeliveryType) && !deliveryTypes.includes(eventsDeliveryType)) {
    eventsDeliveryType = DEFAULT_PRE_CONSENT_EVENTS_DELIVERY_TYPE;

    logger.warn(
      UNSUPPORTED_PRE_CONSENT_EVENTS_DELIVERY_TYPE(
        CONFIG_MANAGER,
        preConsentOpts?.events?.delivery,
        DEFAULT_PRE_CONSENT_EVENTS_DELIVERY_TYPE,
      ),
    );
  }

  batch(() => {
    state.consents.activeConsentManagerPluginName.value = consentManagerPluginName;
    state.consents.initialized.value = initialized;
    state.consents.enabled.value = enabled;
    state.consents.data.value = consentsData;
    state.consents.provider.value = provider;

    state.consents.preConsent.value = {
      // Only enable pre-consent if it is explicitly enabled and
      // if it is not already initialized and
      // if consent management is enabled
      enabled:
        state.loadOptions.value.preConsent?.enabled === true &&
        initialized === false &&
        enabled === true,
      storage: {
        strategy: storageStrategy,
      },
      events: {
        delivery: eventsDeliveryType,
      },
    };
  });
};

/**
 * Determines the consent management state variables from the source config data
 * @param resp Source config response
 * @param logger Logger instance
 */
const updateConsentsState = (resp: SourceConfigResponse): void => {
  let resolutionStrategy: ConsentResolutionStrategy | undefined =
    state.consents.resolutionStrategy.value;

  let cmpMetadata: ConsentManagementMetadata | undefined;
  if (isObjectLiteralAndNotNull(resp.consentManagementMetadata)) {
    if (state.consents.provider.value) {
      resolutionStrategy =
        resp.consentManagementMetadata.providers.find(
          p => p.provider === state.consents.provider.value,
        )?.resolutionStrategy ?? state.consents.resolutionStrategy.value;
    }

    cmpMetadata = resp.consentManagementMetadata;
  }

  // If the provider is custom, then the resolution strategy is not applicable
  if (state.consents.provider.value === 'custom') {
    resolutionStrategy = undefined;
  }

  batch(() => {
    state.consents.metadata.value = clone(cmpMetadata);
    state.consents.resolutionStrategy.value = resolutionStrategy;
  });
};

const updateDataPlaneEventsStateFromLoadOptions = (logger: ILogger) => {
  if (state.dataPlaneEvents.deliveryEnabled.value) {
    const defaultEventsQueuePluginName: PluginName = 'XhrQueue';
    let eventsQueuePluginName: PluginName = defaultEventsQueuePluginName;

    if (state.loadOptions.value.useBeacon) {
      if (state.capabilities.isBeaconAvailable.value) {
        eventsQueuePluginName = 'BeaconQueue';
      } else {
        eventsQueuePluginName = defaultEventsQueuePluginName;

        logger.warn(UNSUPPORTED_BEACON_API_WARNING(CONFIG_MANAGER));
      }
    }

    batch(() => {
      state.dataPlaneEvents.eventsQueuePluginName.value = eventsQueuePluginName;
    });
  }
};

const getSourceConfigURL = (
  configUrl: string | undefined,
  writeKey: string,
  lockIntegrationsVersion: boolean,
  lockPluginsVersion: boolean,
  logger: ILogger,
): string => {
  const defSearchParams = new URLSearchParams({
    p: MODULE_TYPE,
    v: APP_VERSION,
    build: BUILD_TYPE,
    writeKey,
    lockIntegrationsVersion: lockIntegrationsVersion.toString(),
    lockPluginsVersion: lockPluginsVersion.toString(),
  });

  let origin = DEFAULT_CONFIG_BE_URL;
  let searchParams = defSearchParams;
  let pathname = '/sourceConfig/';
  let hash = '';
  if (isValidURL(configUrl)) {
    const configUrlInstance = new URL(configUrl);
    if (!(removeTrailingSlashes(configUrlInstance.pathname) as string).endsWith('/sourceConfig')) {
      configUrlInstance.pathname = `${
        removeTrailingSlashes(configUrlInstance.pathname) as string
      }/sourceConfig/`;
    }
    configUrlInstance.pathname = removeDuplicateSlashes(configUrlInstance.pathname);

    defSearchParams.forEach((value, key) => {
      if (configUrlInstance.searchParams.get(key) === null) {
        configUrlInstance.searchParams.set(key, value);
      }
    });

    origin = configUrlInstance.origin;
    pathname = configUrlInstance.pathname;
    searchParams = configUrlInstance.searchParams;
    hash = configUrlInstance.hash;
  } else {
    logger.warn(INVALID_CONFIG_URL_WARNING(CONFIG_MANAGER, configUrl));
  }

  return `${origin}${pathname}?${searchParams}${hash}`;
};

/**
 * Transforms destinations config from source config response to Destination format
 * @param destinations Array of destination items from config response
 * @returns Array of transformed Destination objects
 */
const getDestinationsFromConfig = (destinations: ConfigResponseDestinationItem[]): Destination[] =>
  destinations.map((destination: ConfigResponseDestinationItem) => ({
    id: destination.id,
    displayName: destination.destinationDefinition.displayName,
    enabled: destination.enabled,
    config: destination.config,
    shouldApplyDeviceModeTransformation: destination.shouldApplyDeviceModeTransformation ?? false,
    propagateEventsUntransformedOnError: destination.propagateEventsUntransformedOnError ?? false,
    userFriendlyId: getDestinationUserFriendlyId(
      destination.destinationDefinition.displayName,
      destination.id,
    ),
    isCustomIntegration:
      destination.destinationDefinition.displayName === CUSTOM_DEVICE_MODE_DESTINATION_DISPLAY_NAME,
  }));

export {
  getSDKUrl,
  updateReportingState,
  updateStorageStateFromLoadOptions,
  updateConsentsStateFromLoadOptions,
  updateConsentsState,
  updateDataPlaneEventsStateFromLoadOptions,
  getSourceConfigURL,
  getDestinationsFromConfig,
};
